'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import examsData from '@/data/exams.json';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ExamEntry {
  id: string;
  name: string;
  emoji: string;
  category: string;
  group: string;
  groupId: string;
  categoryId: string;
  categoryName: string;
}

export interface ExamGroupEntry {
  id: string;
  name: string;
  emoji: string;
  exams: ExamEntry[];
}

export interface ExamCategoryEntry {
  id: string;
  name: string;
  emoji: string;
  groups: ExamGroupEntry[];
}

export interface TargetExam {
  examId: string;
  examName: string;
  examEmoji: string;
  groupId: string;
  groupName: string;
  categoryId: string;
  categoryName: string;
}

interface TargetExamContextValue {
  targetExam: TargetExam | null;
  setTargetExam: (exam: TargetExam) => void;
  categories: ExamCategoryEntry[];
  showExamPicker: boolean;
  examPickerMode: 'change' | 'add';
  openExamPicker: () => void;
  openExamPickerInAddMode: () => void;
  closeExamPicker: () => void;
  isLoadingTargetExam: boolean;
}

// ─── Build categories from exams.json ────────────────────────────────────────

function buildCategories(): ExamCategoryEntry[] {
  return examsData.exam_categories.map((cat) => ({
    id: cat.id,
    name: cat.name,
    emoji: cat.emoji,
    groups: cat.exam_groups.map((grp) => ({
      id: grp.id,
      name: grp.name,
      emoji: grp.emoji,
      exams: grp.exams.map((exam) => ({
        id: exam.id,
        name: exam.name,
        emoji: exam.emoji,
        category: cat.name,
        group: grp.name,
        groupId: grp.id,
        categoryId: cat.id,
        categoryName: cat.name,
      })),
    })),
  }));
}

const STORAGE_KEY = 'minikwa_target_exam';

// ─── Context ──────────────────────────────────────────────────────────────────

const TargetExamContext = createContext<TargetExamContextValue | null>(null);

export function TargetExamProvider({ children }: { children: React.ReactNode }) {
  const [targetExam, setTargetExamState] = useState<TargetExam | null>(null);
  const [isLoadingTargetExam, setIsLoadingTargetExam] = useState(true);
  const [showExamPicker, setShowExamPicker] = useState(false);
  const [examPickerMode, setExamPickerMode] = useState<'change' | 'add'>('change');
  const categories = buildCategories();

  // Load from Hostinger DB on mount (primary source of truth)
  useEffect(() => {
    let cancelled = false;

    async function loadFromDB() {
      try {
        const res = await fetch('/api/minikwa/onboarding');
        if (!res.ok) throw new Error('not_ok');
        const data = await res.json();

        if (cancelled) return;

        if (data.examPrefs && data.examPrefs.target_exam_id) {
          const ep = data.examPrefs;
          // Find matching exam in categories to get full metadata
          let foundExam: TargetExam | null = null;
          for (const cat of categories) {
            for (const grp of cat.groups) {
              const exam = grp.exams.find((e) => e.id === ep.target_exam_id);
              if (exam) {
                foundExam = {
                  examId: exam.id,
                  examName: exam.name,
                  examEmoji: exam.emoji,
                  groupId: grp.id,
                  groupName: grp.name,
                  categoryId: cat.id,
                  categoryName: cat.name,
                };
                break;
              }
            }
            if (foundExam) break;
          }

          // Fallback: use DB-stored name/group if not found in local exam list
          if (!foundExam && ep.target_exam_name) {
            foundExam = {
              examId: ep.target_exam_id,
              examName: ep.target_exam_name,
              examEmoji: ep.target_exam_emoji || '📝',
              groupId: ep.target_exam_group || '',
              groupName: ep.target_exam_group || ep.target_exam_name,
              categoryId: '',
              categoryName: '',
            };
          }

          if (foundExam) {
            setTargetExamState(foundExam);
            // Update localStorage cache
            try {
              localStorage.setItem(STORAGE_KEY, JSON.stringify(foundExam));
            } catch { /* ignore */ }
            setIsLoadingTargetExam(false);
            return;
          }
        }

        // No DB data — clear any stale localStorage default
        if (cancelled) return;
        setTargetExamState(null);
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      } catch {
        if (cancelled) return;
        // DB fetch failed (e.g. unauthenticated) — don't show stale localStorage data
        setTargetExamState(null);
      } finally {
        if (!cancelled) setIsLoadingTargetExam(false);
      }
    }

    loadFromDB();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTargetExam = useCallback((exam: TargetExam) => {
    setTargetExamState(exam);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(exam));
    } catch {
      // ignore
    }
  }, []);

  const openExamPicker = useCallback(() => { setExamPickerMode('change'); setShowExamPicker(true); }, []);
  const openExamPickerInAddMode = useCallback(() => { setExamPickerMode('add'); setShowExamPicker(true); }, []);
  const closeExamPicker = useCallback(() => setShowExamPicker(false), []);

  return (
    <TargetExamContext.Provider value={{ targetExam, setTargetExam, categories, showExamPicker, examPickerMode, openExamPicker, openExamPickerInAddMode, closeExamPicker, isLoadingTargetExam }}>
      {children}
    </TargetExamContext.Provider>
  );
}

export function useTargetExam(): TargetExamContextValue {
  const ctx = useContext(TargetExamContext);
  if (!ctx) throw new Error('useTargetExam must be used within TargetExamProvider');
  return ctx;
}
